/**
 * Pure helpers for the clinical-record redesign: section completion status
 * (drives the stepper dots + close-visit checklist) and summary-panel value
 * formatting. No i18n/JSX here — components translate/format for display.
 */

export type SectionStatus = 'complete' | 'in_progress' | 'pending' | 'optional' | 'locked';

function hasText(v: string | undefined): boolean {
  return typeof v === 'string' && v.trim().length > 0;
}

function ratioStatus(filled: number, total: number): 'complete' | 'in_progress' | 'pending' {
  if (filled <= 0) return 'pending';
  if (filled >= total) return 'complete';
  return 'in_progress';
}

const ANAMNESIS_KEYS = [
  'chief_complaint',
  'symptom_onset_days',
  'previous_illnesses',
  'current_medications',
  'diet_notes',
  'other_notes',
] as const;

const EXAM_KEYS = [
  'body_weight_grams',
  'body_temperature_celsius_tenths',
  'heart_rate_bpm',
  'respiratory_rate_bpm',
  'body_condition_score',
  'cardiovascular_notes',
  'respiratory_notes',
  'gastrointestinal_notes',
  'abdomen_notes',
  'general_impression',
] as const;

const PLAN_KEYS = [
  'treatment_notes',
  'medications_notes',
  'diet_recommendations',
  'activity_restrictions',
  'follow_up_notes',
  'owner_instructions',
] as const;

function countFilled(data: Record<string, string>, keys: readonly string[]): number {
  return keys.filter((k) => hasText(data[k])).length;
}

export function resolveAnamnesisStatus(anam: Record<string, string>): SectionStatus {
  return ratioStatus(countFilled(anam, ANAMNESIS_KEYS), ANAMNESIS_KEYS.length);
}

export function resolveExamStatus(exam: Record<string, string>): SectionStatus {
  return ratioStatus(countFilled(exam, EXAM_KEYS), EXAM_KEYS.length);
}

export function resolvePlanStatus(plan: Record<string, string>): SectionStatus {
  return ratioStatus(countFilled(plan, PLAN_KEYS), PLAN_KEYS.length);
}

/** Mirrors the backend's `examHasContent` for the subset of fields this form exposes —
 *  used only to decide whether the close-visit modal needs the exam justification field.
 *  The server re-validates independently; this is advisory, not enforcement. */
export function examHasContentFrontend(exam: Record<string, string>): boolean {
  return countFilled(exam, EXAM_KEYS) > 0;
}

export function resolveDiagnosesStatus(diagnoses_count: number): SectionStatus {
  return diagnoses_count > 0 ? 'complete' : 'pending';
}

export function resolveAddendaStatus(
  is_addendable: boolean,
  addendums_count: number,
): SectionStatus {
  if (!is_addendable) return 'locked';
  return addendums_count > 0 ? 'complete' : 'optional';
}

export interface ClinicalSummaryValues {
  weight_kg: number | null;
  temp_c: number | null;
  heart_rate_bpm: number | null;
  respiratory_rate_bpm: number | null;
  body_condition_score: number | null;
  has_plan: boolean;
}

function toNum(v: string | undefined): number | null {
  if (v == null || v.trim() === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

export function formatClinicalSummaryValues(
  exam: Record<string, string>,
  plan: Record<string, string>,
): ClinicalSummaryValues {
  const weight_grams = toNum(exam.body_weight_grams);
  const temp_tenths = toNum(exam.body_temperature_celsius_tenths);
  return {
    weight_kg: weight_grams != null ? weight_grams / 1000 : null,
    temp_c: temp_tenths != null ? temp_tenths / 10 : null,
    heart_rate_bpm: toNum(exam.heart_rate_bpm),
    respiratory_rate_bpm: toNum(exam.respiratory_rate_bpm),
    body_condition_score: toNum(exam.body_condition_score),
    has_plan: countFilled(plan, PLAN_KEYS) > 0,
  };
}
